// ── Copy a turnover's pre-job-number files into its job folder (2026-09-28) ──
// Everything uploaded during Contract Review (§1–3 — the signed contract and
// its exhibits included) is saved in the Estimating SharePoint site, because
// the job folder doesn't exist yet. Once the job number is assigned those
// files belong in <entity>/<year> Jobs/<job #> - <project>/Turnover/<NN - Section>.
//
// This replaces the old copy step at job-number assignment, which never copied
// anything: it filtered on a SectionNumber that sp_GetTurnoverDocuments doesn't
// return, only ever meant §2 and §5, ran only when the dialog's folder field was
// filled, and silently skipped any link it couldn't turn into a path.
//
// Here: every file still on the Estimating site is copied, found by its link
// whatever form that link takes (Graph shares API), into its own section
// folder (created if missing), and its link on the turnover is updated. A file
// already in the destination is re-linked, not duplicated. Anything that can't
// be copied is reported — never skipped silently. The original stays where it
// was (copy, not move).
//
// Duplicates (2026-09-28): before copying, the whole job folder is indexed by
// content fingerprint. A file whose content is already anywhere in the job
// folder (under any name, in any folder) is not copied again — the turnover is
// linked to the copy that's there, and the summary says where it was found.
// A different file with the same name as one already in the section folder is
// kept too, under a numbered name.
import { turnoverWrite, payloadOf } from "../../utils/sqlClient";
import { ensureTurnoverSubfolders, listFolderFilesDeep } from "../../utils/sharePoint";
import { buildFileIndex, findDuplicate, addToIndex, duplicateLocation, findJobFolderId, jobFolderParentFor } from "./duplicateFiles";
import { jobFolderName as newJobFolderName } from "./folderPath";
import { subfolderNames, layoutOf } from "../../config/turnoverLayout";
import { shareIdForUrl } from "./packageData";

const GRAPH = "https://graph.microsoft.com/v1.0";

export const isInEstimatingSite = (url) => /\/sites\/Estimating\//i.test(String(url || ""));

// The turnover's files that are still in the Estimating site.
export function docsInEstimatingSite(documents = []) {
  return (documents || []).filter(d => d.SharePointUrl && isInEstimatingSite(d.SharePointUrl));
}

// "07 - Project Estimate & Budget" — the same folder name uploads use.
export const sectionFolderName = (section) =>
  `${String(section?.SectionNumber ?? 0).padStart(2, "0")} - ${String(section?.SectionName || "").replace(/[/\\:*?"<>|]/g, "-")}`;

export const jobFolderName = (turnover) => newJobFolderName(turnover?.JobNumber, turnover?.ProjectName);

async function graphJson(res, what) {
  if (res.ok) return res.json();
  throw new Error(`${what} (${res.status})`);
}

// Copies every Estimating-site file of `turnover` into its job folder.
// turnover needs JobNumber, ProjectName, ContractingEntity, and
// ConstructionFolderItemId when set. Returns { copied, failed }.
export async function copyDocsToJobFolder({ token, constDriveId, turnover, documents, sections, onProgress = () => {} }) {
  const auth = { Authorization: `Bearer ${token}` };
  const docs = docsInEstimatingSite(documents);
  const copied = [], failed = [], linked = [], renamedList = [];
  if (!docs.length) return { copied, failed, linked, renamed: renamedList };

  // The job folder: the saved one, else the one in the prime entity's <year> Jobs named for the job number.
  const jobFolderId = await findJobFolderId(token, constDriveId, turnover);
  if (!jobFolderId) throw new Error(`No job folder for ${String(turnover?.JobNumber || "").trim()} in ${jobFolderParentFor(turnover)} yet — create it (or pick one with ✏️ Change folder) first.`);
  onProgress("Checking the Turnover folders…");
  await ensureTurnoverSubfolders(token, constDriveId, jobFolderId, subfolderNames(layoutOf(turnover)));
  onProgress("Checking the job folder for files already there…");
  const { files: existing } = await listFolderFilesDeep(token, constDriveId, jobFolderId);
  const index = buildFileIndex(existing);

  const bySection = new Map((sections || []).map(s => [s.SectionID, s]));
  const destIds = new Map();
  for (const [i, doc] of docs.entries()) {
    onProgress(`Copying ${i + 1} of ${docs.length}: ${doc.FileName || "file"}…`);
    try {
      const folderName = sectionFolderName(bySection.get(doc.SectionID));
      if (!destIds.has(folderName)) {
        const r = await fetch(`${GRAPH}/drives/${constDriveId}/items/${jobFolderId}:/Turnover/${encodeURIComponent(folderName)}?$select=id`, { headers: auth });
        destIds.set(folderName, (await graphJson(r, `Couldn't open Turnover/${folderName}`)).id);
      }
      const destId = destIds.get(folderName);
      const src = await graphJson(await fetch(`${GRAPH}/shares/${shareIdForUrl(doc.SharePointUrl)}/driveItem?$select=id,name,size,file,parentReference`, { headers: auth }), "Couldn't find the file in the Estimating site");
      const srcHash = src.file?.hashes?.quickXorHash || null;

      // Already somewhere in the job folder (any name, any folder) → link to it.
      const dup = findDuplicate(index, srcHash);
      if (dup) {
        await turnoverWrite(`EXEC sp_UpdateDocumentSharePointUrl @payload = '${payloadOf({ turnover_document_id: doc.TurnoverDocumentID, sharepoint_url: dup.webUrl })}'`);
        linked.push({ documentId: doc.TurnoverDocumentID, fileName: doc.FileName, foundAt: duplicateLocation(dup), newUrl: dup.webUrl });
        continue;
      }

      const copyTo = (behavior) => fetch(`${GRAPH}/drives/${src.parentReference.driveId}/items/${src.id}/copy?@microsoft.graph.conflictBehavior=${behavior}`, {
        method: "POST", headers: { ...auth, "Content-Type": "application/json" },
        body: JSON.stringify({ parentReference: { driveId: constDriveId, id: destId }, name: src.name }),
      });
      let copy = await copyTo("fail");
      let renamed = false;
      if (copy.status === 409) {
        // Same name in the section folder but (the index says) different content — keep both.
        copy = await copyTo("rename");
        renamed = true;
      }
      if (!copy.ok) throw new Error(`Copy refused (${copy.status})`);
      let newItemId = null;
      const monitor = copy.headers.get("Location");
      for (let n = 0; monitor && n < 40; n++) {
        const st = await fetch(monitor).then(r => r.json()).catch(() => ({}));
        if (st.status === "completed") { newItemId = st.resourceId || null; break; }
        if (st.status === "failed") throw new Error("SharePoint reported the copy failed");
        await new Promise(r => setTimeout(r, 1500));
      }
      const dest = newItemId
        ? await graphJson(await fetch(`${GRAPH}/drives/${constDriveId}/items/${newItemId}?$select=id,name,webUrl`, { headers: auth }), "The copied file couldn't be found")
        : await graphJson(await fetch(`${GRAPH}/drives/${constDriveId}/items/${destId}:/${encodeURIComponent(src.name)}?$select=id,name,webUrl`, { headers: auth }), "The copied file couldn't be found");
      await turnoverWrite(`EXEC sp_UpdateDocumentSharePointUrl @payload = '${payloadOf({ turnover_document_id: doc.TurnoverDocumentID, sharepoint_url: dest.webUrl })}'`);
      addToIndex(index, { id: dest.id, name: dest.name, webUrl: dest.webUrl, hash: srcHash, path: `Turnover/${folderName}/${dest.name}` });
      if (renamed) renamedList.push({ fileName: doc.FileName, savedAs: dest.name });
      copied.push({ documentId: doc.TurnoverDocumentID, fileName: doc.FileName, folder: folderName, newUrl: dest.webUrl, savedAs: dest.name });
    } catch (e) {
      failed.push({ documentId: doc.TurnoverDocumentID, fileName: doc.FileName, reason: e.message });
    }
  }
  return { copied, failed, linked, renamed: renamedList };
}

export function copySummary({ copied = [], failed = [], linked = [], renamed = [] }) {
  const lines = [];
  if (copied.length) lines.push(`${copied.length} file${copied.length === 1 ? "" : "s"} copied into the job folder.`);
  if (linked.length) lines.push(`Already in the job folder, so linked instead of copied: ${linked.map(l => `${l.fileName} (at ${l.foundAt})`).join("; ")}.`);
  if (renamed.length) lines.push(`Kept both (same name, different file): ${renamed.map(r => `${r.fileName} → ${r.savedAs}`).join("; ")}.`);
  if (failed.length) lines.push(`Not copied: ${failed.map(f => `${f.fileName} (${f.reason})`).join("; ")}`);
  return lines.join(" ") || "Nothing to copy — every file is already in the job folder.";
}
